import { CheckIcon } from "components/icons/icn-check-status-projects.icon";
import CloseIcon from "components/icons/icn-close.icon";
import { IconTrash } from "components/icons/icn-index.icon";
import { getLocaleCode } from "locales";
import intl from "react-intl-universal";
import {
  SelectionToolbarActions,
  SelectionToolbarCard,
  SelectionToolbarCloseButton,
  SelectionToolbarDeleteButton,
  SelectionToolbarIcon,
  SelectionToolbarOverlay,
  SelectionToolbarSummary,
  SelectionToolbarSummaryText,
} from "./styled";

export interface RowSelectionToolbarProps {
  readonly selectedCount: number;
  readonly onClose: () => void;
  readonly onDelete?: () => void;
  readonly deleteDisabled?: boolean;
}

const formatCount = (selectedCount: number) =>
  new Intl.NumberFormat(getLocaleCode()).format(selectedCount);

const getDefaultSelectionToolbarMessages = () => ({
  selectionSummary: (selectedCount: number) =>
    intl.get(
      selectedCount === 1
        ? "commons.table.selectionToolbar.selectionSummarySingular"
        : "commons.table.selectionToolbar.selectionSummaryPlural",
      { count: formatCount(selectedCount) }
    ),
  delete: intl.get("commons.delete"),
  deleteAriaLabel: (selectedCount: number) =>
    intl.get(
      selectedCount === 1
        ? "commons.table.selectionToolbar.deleteAriaLabelSingular"
        : "commons.table.selectionToolbar.deleteAriaLabelPlural",
      { count: formatCount(selectedCount) }
    ),
  closeAriaLabel: intl.get("commons.table.selectionToolbar.closeAriaLabel"),
  toolbarAriaLabel: intl.get("commons.table.selectionToolbar.toolbarAriaLabel"),
});

const RowSelectionToolbar = ({
  selectedCount,
  onClose,
  onDelete,
  deleteDisabled = false,
}: RowSelectionToolbarProps) => {
  const copy = getDefaultSelectionToolbarMessages();
  const summary = copy.selectionSummary(selectedCount);
  const deleteLabel = copy.delete;

  return (
    <SelectionToolbarOverlay>
      <SelectionToolbarCard role="toolbar" aria-label={copy.toolbarAriaLabel}>
        <SelectionToolbarSummary>
          <SelectionToolbarIcon aria-hidden="true">
            <CheckIcon width={16} height={16} color="var(--color-neutral-600, #0e0e0e)" />
          </SelectionToolbarIcon>

          <SelectionToolbarSummaryText aria-live="polite">{summary}</SelectionToolbarSummaryText>
        </SelectionToolbarSummary>

        <SelectionToolbarActions>
          <SelectionToolbarDeleteButton
            type="button"
            onClick={onDelete}
            disabled={deleteDisabled}
            aria-label={copy.deleteAriaLabel(selectedCount)}
          >
            <IconTrash width={16} height={16} color="currentColor" />
            <span>{deleteLabel}</span>
          </SelectionToolbarDeleteButton>
    
          <SelectionToolbarCloseButton
            type="button"
            onClick={onClose}
            aria-label={copy.closeAriaLabel}
          >
            <CloseIcon width={16} height={16} />
          </SelectionToolbarCloseButton>
        </SelectionToolbarActions>
      </SelectionToolbarCard>
    </SelectionToolbarOverlay>
  );
};

export default RowSelectionToolbar;
